import { useRef, useEffect, useState } from 'react';

/**
 * Video di sfondo in loop costante.
 * - Forza muted + play via ref per garantire l'autoplay su tutti i browser
 *   (iOS/Safari incluso) ed evitare la comparsa del pulsante play nativo.
 * - Lazy-load: il sorgente viene caricato solo quando il video si avvicina al
 *   viewport (IntersectionObserver). Fino ad allora resta visibile solo il poster,
 *   così i video sotto la piega non rallentano il caricamento iniziale (LCP).
 */
export const LoopVideo = ({ src, poster, className = '', testid }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!v || !visible) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.setAttribute('webkit-playsinline', 'true');

    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    v.addEventListener('canplay', tryPlay, { once: true });
    v.addEventListener('loadeddata', tryPlay, { once: true });
    return () => {
      v.removeEventListener('canplay', tryPlay);
      v.removeEventListener('loadeddata', tryPlay);
    };
  }, [visible, src]);

  return (
    <video
      ref={ref}
      src={visible ? src : undefined}
      poster={poster}
      autoPlay
      loop
      muted
      playsInline
      preload={visible ? 'auto' : 'none'}
      controls={false}
      disablePictureInPicture
      tabIndex={-1}
      className={className}
      data-testid={testid}
    />
  );
};
